import { IDV360TicketsCreative } from '@/Interfaces/DV360Tickets/Creative.interface';
import type { IProduct } from '@/Interfaces/Product.interface';
import CreateCreativeIcon from '@/components/Icons/CreateCreativeIcon';
import ResetAudienceIcon from '@/components/Icons/ResetAudienceIcon';
import { getColumns } from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/Creatives/Table/columns';
import CreativesTable from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Table';
import { useAppSelector } from '@/store/hooks';
import { Add } from '@mui/icons-material';
import { Button } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import DV360TicketsAssociateCreativesModal from './Modal';

const DV360TicketsCreativesSection = () => {
  const loading: boolean = !!useAppSelector(({ campaignManagement }) => campaignManagement.activeCampaignRequestId);

  const { setValue } = useFormContext();

  const associatedCreatives: IDV360TicketsCreative[] = useWatch({ name: 'creatives' });
  const product: IProduct | null = useWatch({ name: 'product' });

  const originalAssociatedCreatives: IDV360TicketsCreative[] = useMemo(() => [...associatedCreatives], []);

  const rows = associatedCreatives.map((creative, index) => ({
    ...creative,
    creativeIndex: index,
  }));

  const [isDirty, setIsDirty] = useState(false);
  const [isAssociateCreativesModalOpen, setIsAssociateCreativesModalOpen] = useState(false);
  const [rowCreativeTableSelectionModel, setRowCreativeTableSelectionModel] = useState<GridRowSelectionModel>([]);

  const creativeIds = useMemo(() => associatedCreatives.map(creative => creative.id), [associatedCreatives]);

  const hasAssociatedCreatives = !loading && associatedCreatives?.length > 0;

  const setAssociatedCreatives = useCallback(
    (creatives: IDV360TicketsCreative[]) => {
      setValue('creatives', creatives, { shouldDirty: true, shouldValidate: true });
      setIsDirty(true);
    },
    [setValue],
  );

  const handleFieldChange = useCallback(
    (id: number, field: keyof Pick<IDV360TicketsCreative, 'disassociated'>) => (value: any) => {
      setAssociatedCreatives(
        associatedCreatives.map(creative => (creative.id === id ? { ...creative, [field]: value } : creative)),
      );
    },
    [associatedCreatives, setAssociatedCreatives],
  );

  const handleResetChanges = useCallback(() => {
    setAssociatedCreatives(originalAssociatedCreatives);
    setIsDirty(false);
  }, [setAssociatedCreatives, originalAssociatedCreatives]);

  const handleAssociateCreatives = useCallback(
    (newAssociatedCreatives: IDV360TicketsCreative[]) => {
      setAssociatedCreatives([...associatedCreatives, ...newAssociatedCreatives]);
    },
    [associatedCreatives, setAssociatedCreatives],
  );

  const columns = useMemo(
    () =>
      getColumns({
        creativeIds,
        handleFieldChange,
      }),
    [creativeIds, handleFieldChange],
  );

  return (
    <div className='w-full'>
      <div
        className={classNames(
          'flex items-center',
          hasAssociatedCreatives ? 'justify-between' : 'flex-col justify-center gap-6 h-full',
        )}
      >
        {hasAssociatedCreatives ? (
          <span className='text-center text-lg text-[#1E1E1E] font-semibold'>Associated Creatives</span>
        ) : (
          <div className='flex flex-col justify-center items-center gap-6'>
            <CreateCreativeIcon width={202} height={205} />
            <span className='text-xl font-semibold'>No Creatives Associated Yet</span>
          </div>
        )}
        <div className='flex gap-2'>
          {isDirty && (
            <Button variant='outlined' startIcon={<ResetAudienceIcon />} onClick={handleResetChanges}>
              Reset Changes
            </Button>
          )}
          <Button variant='contained' disabled={!product} onClick={() => setIsAssociateCreativesModalOpen(true)}>
            <Add sx={{ width: 20, height: 20, marginRight: '4px' }} />
            Associate New {!hasAssociatedCreatives && 'Creative'}
          </Button>
        </div>
      </div>
      {product && (
        <DV360TicketsAssociateCreativesModal
          open={isAssociateCreativesModalOpen}
          handleClose={() => setIsAssociateCreativesModalOpen(false)}
          associatedCreatives={associatedCreatives}
          handleAssociateCreatives={handleAssociateCreatives}
          product={product}
        />
      )}
      {hasAssociatedCreatives && (
        <div className='my-4'>
          <CreativesTable
            tableData={rows}
            columns={columns}
            checkboxSelection={false}
            rowSelectionModel={rowCreativeTableSelectionModel}
            handleRowSelectionModelChange={setRowCreativeTableSelectionModel}
            withoutPagination
            setTableFilters={() => null}
          />
        </div>
      )}
    </div>
  );
};

export default DV360TicketsCreativesSection;
